/**
 * QorTrace · Landing — Iter237
 *
 * Block 1 — Product 4-up as a 2x2 bento grid (NOT a 4-wide horizontal
 * stack). Each tile has a hover-glow border + the step number renders
 * as oversized translucent display type behind the content so each
 * tile has its own visual focal point.
 *
 * Design spec: `/app/design_guidelines.json` → block-1-product-grid.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, FileCode2, ShieldCheck, LayoutDashboard } from "lucide-react";


const TILES = [
  {
    to: "/scan", testid: "product-tile-scan",
    step: "01", verb: "SCAN", tag: "QORTRACE SCAN",
    Ic: ShieldAlert,
    title: "Wallet Quantum Exposure",
    body:  "Paste any address across 18 chains. Deterministic 0–100 exposure score, factor breakdown, and migration playbook in seconds. Free and unauthenticated.",
    cta: "Run a free scan",
    accent: "#00E5FF",
  },
  {
    to: "/contract", testid: "product-tile-contract",
    step: "02", verb: "ANALYZE", tag: "QORTRACE CONTRACT",
    Ic: FileCode2,
    title: "Smart Contract PQC Audit",
    body:  "Static analysis for Solidity. Catches ecrecover, EIP-712, permit, BLS pairings, hardcoded signers — every PQC migration surface, line-numbered.",
    cta: "Analyze a contract",
    accent: "#00E5FF",
  },
  {
    to: "/audit", testid: "product-tile-audit",
    step: "03", verb: "CERTIFY", tag: "QORTRACE AUDIT",
    Ic: ShieldCheck,
    title: "Full Audits + Signed Certificates",
    body:  "Standard (AI-narrated, <90s) or Deep Dive (senior auditor reviewed). Severity-tiered findings, Security + Trust Scores, branded shareable cert.",
    cta: "Get audited",
    accent: "#1FDD83",
  },
  {
    to: "/atlas", testid: "product-tile-atlas",
    step: "04", verb: "MONITOR", tag: "QORTRACE ATLAS",
    Ic: LayoutDashboard,
    title: "Treasury Exposure Dashboard",
    body:  "Aggregate balance-at-risk, exposed-address counts, and contract finding heatmaps across your entire on-chain surface — with webhooks and CSV import.",
    cta: "Open Atlas",
    accent: "#1FDD83",
  },
];


export default function ProductBentoGrid() {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
      data-testid="product-bento-grid"
    >
      {TILES.map((t) => {
        const Ic = t.Ic;
        return (
          <Link
            key={t.testid}
            to={t.to}
            data-testid={t.testid}
            className="relative group block overflow-hidden rounded-md"
            style={{
              padding: "32px",
              background: "#0B1324",
              border: "1px solid rgba(255,255,255,0.06)",
              minHeight: 260,
              textDecoration: "none",
              transition: "border-color 240ms ease, transform 240ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = `${t.accent}66`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
            }}
          >
            {/* Oversized translucent step number — backdrop focal point. */}
            <span
              aria-hidden="true"
              className="qt-mono"
              style={{
                position: "absolute",
                top: -18, right: -10,
                fontSize: 130, lineHeight: 1, fontWeight: 800,
                color: "rgba(255,255,255,0.025)",
                letterSpacing: "-0.04em",
                pointerEvents: "none",
              }}
            >
              {t.step}
            </span>

            {/* Icon — top-right, accent color. */}
            <div
              style={{
                position: "absolute", top: 24, right: 24,
                color: t.accent,
                transition: "transform 240ms ease",
              }}
              className="group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
            >
              <Ic size={22} />
            </div>

            <div
              className="qt-mono"
              style={{
                fontSize: 11, letterSpacing: "0.20em",
                color: t.accent, fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {t.tag}
            </div>

            <h3
              className="qt-h2"
              style={{
                fontSize: 26, lineHeight: 1.15, marginTop: 12,
                color: "#fff", maxWidth: "92%",
              }}
            >
              {t.title}
            </h3>

            <p
              className="text-sm"
              style={{
                color: "var(--qt-text-2)", marginTop: 14,
                lineHeight: 1.6, maxWidth: "94%",
              }}
            >
              {t.body}
            </p>

            <div
              className="qt-mono mt-6 inline-flex items-center gap-2"
              style={{
                color: t.accent, fontSize: 11, letterSpacing: "0.18em",
                textTransform: "uppercase", fontWeight: 700,
              }}
            >
              {t.cta} <ArrowRight size={12} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}
